<template>
  <div data-component="app-launcher-header">
    <div
      class="title-block"
      @click="$emit('go-back')"
    >
      <or-icon icon="arrow_back" />

      <div class="title">
        {{ $t('HITL.app-launcher.title') }}
      </div>
    </div>

    <div
      v-if="!!agent"
      class="buttons-block"
    >
      <app-launcher-header-actions
        v-if="!!actionsType"
        :actions-type="actionsType"
        :label="saveButtonLabel"
        :is-updating="isUpdating"
        @save="$emit('save')"
        @update="$emit('update')"
        @cancel="$emit('cancel')"
      />

      <div
        v-if="!!actionsType"
        class="divider"
      />

      <app-launcher-header-agent-popup
        :avatar="avatar"
        :agent="agent"
        :app-version="appVersion"
        :options="options"
        @option-click="onOptionClick"
        @sign-out="$emit('sign-out')"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { OrIconV3 as OrIcon } from '@onereach/ui-components-25';

import AppLauncherHeaderActions from './AppLauncherHeaderActions.vue';
import AppLauncherHeaderAgentPopup from './AppLauncherHeaderAgentPopup.vue';
import { AVATAR_PROP, AGENT_PROP, ACTIONS_TYPE_PROP } from '../constants';

export default defineComponent({
  name: 'AppLauncherHeader',
  components: {
    OrIcon,
    AppLauncherHeaderActions,
    AppLauncherHeaderAgentPopup,
  },
  props: {
    avatar: AVATAR_PROP,
    agent: AGENT_PROP,
    actionsType: ACTIONS_TYPE_PROP,
    appVersion: {
      type: String,
      default: '',
    },
    isUpdating: Boolean,
  },
  emits: ['go-back', 'option-click', 'save', 'update', 'cancel', 'sign-out'],
  computed: {
    saveButtonLabel() {
      switch (this.actionsType) {
        case 'save':
          return this.$t('HITL.common.actions.save');
        case 'update':
          return this.$t('HITL.common.actions.update');
        default:
          return '';
      }
    },
    options() {
      const OPTIONS = [
        { key: 'confirm', label: this.saveButtonLabel },
        { key: 'cancel', label: this.$t('HITL.common.actions.cancel') },
      ];
      return this.actionsType ? OPTIONS : [];
    },
  },
  methods: {
    onOptionClick(key: string) {
      switch (key) {
        case 'confirm':
          this.$emit(this.actionsType);
          break;
        case 'cancel':
          this.$emit('cancel');
          break;
        default:
          this.$emit('option-click', key);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='app-launcher-header'] {
  display: flex;
  align-items: center;
  gap: var(--s-4);
  height: 4.3125rem;
  padding: var(--s-4) var(--s-5);
  background: var(--or-c-background);
  border-bottom: 1px solid var(--or-c-outline-variant);

  /** TODO: Use modal when in mobile layout? */
  @include max-md {
    [data-component='app-launcher-header-actions'],
    .divider {
      display: none !important;
    }
  }

  .title-block,
  .buttons-block {
    display: flex;
    align-items: center;
  }

  .title-block {
    gap: var(--s-3);
    color: var(--or-c-on-background);
    cursor: pointer;

    .title {
      flex: 0 0 auto;
      font-size: 1.25rem;
      font-weight: var(--fw-2);
      line-height: 1.5;
    }

    .or-icon-v3 {
      height: 1.25rem;
      width: 1.25rem;
      font-size: 1.25rem;
    }
  }

  .buttons-block {
    margin-left: auto;

    .divider {
      width: 1px;
      height: 2.25rem;
      background: var(--or-c-outline-variant);
      margin: 0 var(--s-6);
    }
  }
}
</style>
